<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.addPackage') }}</span>
        </div>
      </template>

      <el-form :model="vipForm" :rules="rules" ref="formRef" label-width="140px">
        <el-form-item :label="$t('vip.name')" prop="name">
          <el-input 
            v-model="vipForm.name" 
            :placeholder="$t('vip.enterName')"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('vip.packageType')" prop="packageTypeId">
          <div style="display: flex; gap: 8px; width: 100%">
            <el-select 
              v-model="vipForm.packageTypeId" 
              :placeholder="$t('vip.selectPackageType')"
              style="flex: 1"
              filterable
              clearable
            >
              <el-option
                v-for="type in vipStore.packageTypes"
                :key="type.id"
                :label="type.name"
                :value="type.id"
              />
            </el-select>
            <el-button 
              type="primary" 
              @click="showAddPackageTypeDialog"
              :icon="Plus"
            >
              {{ $t('common.add') }}
            </el-button>
          </div>
        </el-form-item>

        <el-form-item :label="$t('vip.price')" prop="price">
          <el-input-number 
            v-model="vipForm.price" 
            :min="0.01" 
            :max="999999.99"
            :precision="2" 
            :step="100"
            style="width: 100%"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.courseHours')" prop="courseHours">
          <el-input-number 
            v-model="vipForm.courseHours" 
            :min="1" 
            :max="9999" 
            :step="1"
            style="width: 100%"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.validityDays')" prop="validityDays">
          <el-input-number 
            v-model="vipForm.validityDays" 
            :min="1" 
            :max="9999" 
            :step="1"
            style="width: 100%"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.isGiftPackage')" prop="isGiftPackage">
          <el-switch 
            v-model="vipForm.isGiftPackage"
            :active-text="$t('common.yes')"
            :inactive-text="$t('common.no')"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.description')" prop="description">
          <el-input 
            v-model="vipForm.description" 
            type="textarea" 
            :rows="3"
            :placeholder="$t('vip.enterDescription')"
          />
        </el-form-item>

        <el-form-item>
          <el-button @click="handleCancel">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">
            {{ $t('common.submit') }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Add Package Type Dialog -->
    <el-dialog 
      v-model="packageTypeDialogVisible" 
      :title="$t('vip.addPackageType')"
      width="500px"
      destroy-on-close
      :close-on-click-modal="false"
    >
      <el-form :model="packageTypeForm" :rules="packageTypeRules" ref="packageTypeFormRef" label-width="120px">
        <el-form-item :label="$t('vip.typeName')" prop="name">
          <el-input 
            v-model="packageTypeForm.name" 
            :placeholder="$t('vip.enterTypeName')"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item :label="$t('vip.typeDescription')" prop="description">
          <el-input 
            v-model="packageTypeForm.description" 
            type="textarea" 
            :rows="3"
            :placeholder="$t('vip.enterTypeDescription')"
          />
        </el-form-item>

        <el-form-item :label="$t('poster.order')" prop="displayOrder">
          <el-input-number 
            v-model="packageTypeForm.displayOrder" 
            :min="0" 
            :max="9999" 
            :step="1"
            style="width: 100%"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item :label="$t('vip.status')" prop="isActive">
          <el-switch 
            v-model="packageTypeForm.isActive"
            :active-text="$t('vip.active')"
            :inactive-text="$t('vip.inactive')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="packageTypeDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitPackageTypeForm" :loading="packageTypeSubmitting">
          {{ $t('common.submit') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { useVipStore } from '@/stores/vip'

const router = useRouter()
const { t } = useI18n()
const vipStore = useVipStore()
const formRef = ref()
const submitting = ref(false)
const packageTypeDialogVisible = ref(false)
const packageTypeSubmitting = ref(false)
const packageTypeFormRef = ref()

const vipForm = reactive({
  name: '',
  price: 0,
  courseHours: 0,
  validityDays: 0,
  description: '',
  packageTypeId: null,
  isGiftPackage: false
})

const rules = {
  name: [
    { required: true, message: 'Please enter package name', trigger: 'blur' },
    { min: 1, max: 100, message: 'Package name must be between 1-100 characters', trigger: 'blur' }
  ],
  price: [
    { required: true, message: 'Please enter price', trigger: 'blur' },
    { type: 'number', min: 0.01, max: 999999.99, message: 'Price must be between 0.01 and 999999.99', trigger: 'blur' }
  ],
  courseHours: [
    { required: true, message: 'Please enter course hours', trigger: 'blur' },
    { type: 'number', min: 1, max: 9999, message: 'Course hours must be between 1 and 9999', trigger: 'blur' }
  ],
  validityDays: [
    { required: true, message: 'Please enter validity days', trigger: 'blur' },
    { type: 'number', min: 1, max: 9999, message: 'Validity days must be between 1 and 9999', trigger: 'blur' }
  ],
  packageTypeId: [
    { required: true, message: 'Please select package type', trigger: 'change' }
  ]
}

const packageTypeForm = reactive({
  name: '',
  description: '',
  displayOrder: 0,
  isActive: true
})

const packageTypeRules = {
  name: [
    { required: true, message: 'Please enter package type name', trigger: 'blur' },
    { min: 1, max: 100, message: 'Name must be between 1-100 characters', trigger: 'blur' }
  ],
  displayOrder: [
    { required: true, message: 'Please enter display order', trigger: 'blur' },
    { type: 'number', min: 0, max: 9999, message: 'Display order must be between 0 and 9999', trigger: 'blur' }
  ]
}

onMounted(async () => {
  // Load package types
  await vipStore.fetchPackageTypes(true) // Only active types
})

const submitForm = async () => {
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    try {
      const result = await vipStore.createPackage(vipForm)
      
      if (result.success) {
        ElMessage.success(t('vip.addSuccess'))
        router.push('/vip')
      } else {
        ElMessage.error(result.message || t('vip.addFailed'))
      }
    } catch (error) {
      ElMessage.error(t('vip.addFailed'))
    } finally {
      submitting.value = false
    }
  })
}

const handleCancel = () => {
  router.back()
}

const showAddPackageTypeDialog = () => {
  resetPackageTypeForm()
  packageTypeDialogVisible.value = true
}

const submitPackageTypeForm = async () => {
  await packageTypeFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    packageTypeSubmitting.value = true
    try {
      const result = await vipStore.createPackageType(packageTypeForm)
      
      if (result.success) {
        ElMessage.success(t('vip.addSuccess'))
        packageTypeDialogVisible.value = false
        // Auto-select the newly created package type
        if (result.data && result.data.id) {
          vipForm.packageTypeId = result.data.id
        }
      } else {
        ElMessage.error(result.message || t('vip.addFailed'))
      }
    } catch (error) {
      ElMessage.error(t('vip.addFailed'))
    } finally {
      packageTypeSubmitting.value = false
    }
  })
}

const resetPackageTypeForm = () => {
  packageTypeForm.name = ''
  packageTypeForm.description = ''
  packageTypeForm.displayOrder = 0
  packageTypeForm.isActive = true
}
</script>
